import React from 'react';
import { Menu, CanAccess } from 'react-admin';
import CatalogIcon from '@mui/icons-material/Inventory';
import SwitchAccountIcon from '@mui/icons-material/SwitchAccount';
import ApiIcon from '@mui/icons-material/Code';
import RepairIcon from '@mui/icons-material/Build';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import StorefrontIcon from '@mui/icons-material/Storefront';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import InsightsIcon from '@mui/icons-material/Insights';
import WifiIcon from '@mui/icons-material/Wifi';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import SettingsIcon from '@mui/icons-material/Settings';
import { DashboardResources } from '@src/types';
import { useLookerActive } from '@src/lookerDashboards';
import { useSftpFoldersConfigured } from '@src/files';
import { STOREFRONT_TABS } from '@src/storefront';
import { DEVELOPER_TABS } from '@src/developer';
import { CATALOG_TABS } from './catalog';
import { REPAIRS_TABS } from './repairs';
import { SETTINGS_TABS } from './plugins';
import { useVisibleTabs, type SectionTab } from './SectionTabShell';

/**
 * Applies active-state color to the Menu.Item icon. react-admin's MenuItemLink
 * sets text.primary on the active item but doesn't propagate to ListItemIcon.
 * This sx targets the icon within the RaMenuItemLink-active class.
 */
const activeIconSx = {
  '&.RaMenuItemLink-active .MuiListItemIcon-root': {
    color: 'text.primary',
  },
};

/**
 * Renders a tabbed section's nav item only when the user can see at least one
 * of its tabs. If the permission check itself failed, keep the entry — dropping
 * nav on a transport error looks identical to losing access, and the section
 * root re-checks and reports the real problem when they click through.
 */
const SectionMenuItem = ({ tabs, children }: { tabs: SectionTab[], children: React.ReactElement }) => {
  const { visibleTabs, error } = useVisibleTabs(tabs);
  return visibleTabs.length || error ? children : null;
};

/**
 * Top-level nav. Catalog, Storefront, Resale Pricing, Repairs, Developer and Settings are
 * single entries whose subpages render as tabs (see SectionTabShell).
 */
export const CustomMenu = () => {
  const isLookerActive = useLookerActive();
  const { isConfigured: isSftpFoldersConfigured } = useSftpFoldersConfigured();

  return (
    <Menu>
      {isLookerActive && (
        <CanAccess resource={DashboardResources.LookerDashboards} action="show">
          <Menu.Item
            to={`/${DashboardResources.LookerDashboards}`}
            primaryText="Analytics"
            leftIcon={<InsightsIcon />}
            sx={activeIconSx}
          />
        </CanAccess>
      )}
      <CanAccess resource="partner-select" action="show">
        <Menu.Item
          to="/partners"
          primaryText="Select partner"
          leftIcon={<SwitchAccountIcon />}
          sx={activeIconSx}
        />
      </CanAccess>
      <SectionMenuItem tabs={CATALOG_TABS}>
        <Menu.Item
          to="/catalog"
          primaryText="Catalog"
          leftIcon={<CatalogIcon />}
          sx={activeIconSx}
        />
      </SectionMenuItem>
      <CanAccess resource={DashboardResources.InventoryItems} action="list">
        <Menu.Item
          to={`/${DashboardResources.InventoryItems}`}
          primaryText="Inventory"
          leftIcon={<Inventory2Icon />}
          sx={activeIconSx}
        />
      </CanAccess>
      <CanAccess resource="orders" action="list">
        <Menu.Item
          to="/orders"
          primaryText="Orders"
          leftIcon={<ReceiptLongIcon />}
          sx={activeIconSx}
        />
      </CanAccess>
      <CanAccess resource="tradeins" action="list">
        <Menu.Item
          to="/tradeins"
          primaryText="Trade-ins"
          leftIcon={<SwapHorizIcon />}
          sx={activeIconSx}
        />
      </CanAccess>
      <CanAccess resource="giftcards" action="list">
        <Menu.Item
          to="/giftcards"
          primaryText="Gift cards"
          leftIcon={<CardGiftcardIcon />}
          sx={activeIconSx}
        />
      </CanAccess>
      <SectionMenuItem tabs={STOREFRONT_TABS}>
        <Menu.Item
          to="/storefront"
          primaryText="Storefront"
          leftIcon={<StorefrontIcon />}
          sx={activeIconSx}
        />
      </SectionMenuItem>
      <CanAccess resource={DashboardResources.PricingRules} action="list">
        <Menu.Item
          to="/resale"
          primaryText="Resale Pricing"
          leftIcon={<AttachMoneyIcon />}
          sx={activeIconSx}
        />
      </CanAccess>
      <SectionMenuItem tabs={REPAIRS_TABS}>
        <Menu.Item
          to="/repairs"
          primaryText="Repairs"
          leftIcon={<RepairIcon />}
          sx={activeIconSx}
        />
      </SectionMenuItem>
      {isSftpFoldersConfigured && (
        <CanAccess resource={DashboardResources.Files} action="list">
          <Menu.Item
            to={`/${DashboardResources.Files}`}
            primaryText="Data Feeds"
            leftIcon={<WifiIcon />}
            sx={activeIconSx}
          />
        </CanAccess>
      )}
      <SectionMenuItem tabs={DEVELOPER_TABS}>
        <Menu.Item
          to="/dev"
          primaryText="Developer"
          leftIcon={<ApiIcon />}
          sx={activeIconSx}
        />
      </SectionMenuItem>
      <SectionMenuItem tabs={SETTINGS_TABS}>
        <Menu.Item
          to="/settings"
          primaryText="Settings"
          leftIcon={<SettingsIcon />}
          sx={activeIconSx}
        />
      </SectionMenuItem>
    </Menu>
  );
};

export default CustomMenu;
